import { OFFLINE_CACHE_PREFIX } from '@/offline-service-worker/routing-rules'
import sentry, { featureTags } from '@/services/sentry'

export const OFFLINE_SERVICE_WORKER_FILENAME = 'offline-sw.js'

const FLAGS_READY_TIMEOUT_MS = 15 * 1000
const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1', '[::1]']

const captureOfflineServiceWorkerError = (error, variant) => sentry.captureException(error, {
  tags: {
    ...featureTags({ featureFlag: 'PRE-204-offline-presenting', featureName: 'offline-service-worker', taskId: 'PRE-208' }),
    feature_variant: variant,
  },
})

/**
 * Whether this page may run the offline service worker at all, before the flag is read.
 * The PowerPoint add-in, Google Slides and embedded frames keep today's behaviour.
 */
export const canHostOfflineServiceWorker = ({
  location,
  environment,
  serviceWorkerUrl,
  localOverride,
  isAddinBuild,
  integrationPlatform,
  isTopWindow,
  hasServiceWorkerApi,
}) => {
  if (!hasServiceWorkerApi || !isTopWindow) return false
  if (isAddinBuild || integrationPlatform) return false
  const isLocal = environment === 'local' || LOCAL_HOSTNAMES.includes(location.hostname)
  if (isLocal && localOverride !== '1') return false
  try {
    return new URL(serviceWorkerUrl, location.href).origin === location.origin
  } catch {
    return false
  }
}

const MOBILE_USER_AGENT = /iPhone|iPod|iPad|Android|Mobi|Tablet|Silk|Kindle|PlayBook/i

/**
 * Whether the browser runs on a phone or tablet, where the offline caches would take device storage.
 * Uses the user agent, never the viewport, so a narrow desktop window still counts as desktop.
 */
export const isMobileDevice = ({
  userAgentData, userAgent = '', platform, maxTouchPoints = 0,
} = {}) => {
  if (userAgentData?.mobile) return true
  if (MOBILE_USER_AGENT.test(userAgent)) return true
  // iPadOS reports itself as a Mac; only the touch screen gives it away.
  return platform === 'MacIntel' && maxTouchPoints > 1
}

const isOfflineServiceWorkerRegistration = (registration) => [
  registration.active, registration.waiting, registration.installing,
].some((worker) => worker?.scriptURL?.endsWith(`/${OFFLINE_SERVICE_WORKER_FILENAME}`))

const deleteOfflineCaches = async (cacheStorage) => {
  if (!cacheStorage) return
  const cacheNames = await cacheStorage.keys()
  await Promise.all(cacheNames
    .filter((name) => name.startsWith(OFFLINE_CACHE_PREFIX))
    .map((name) => cacheStorage.delete(name)))
}

/**
 * Unregisters only the offline worker (OneSignal's push worker stays) and deletes its caches.
 * Returns the number of registrations removed.
 */
const removeOfflineServiceWorker = async (serviceWorkerContainer, cacheStorage) => {
  const registrations = await serviceWorkerContainer.getRegistrations()
  const ours = registrations.filter(isOfflineServiceWorkerRegistration)
  const unregistering = Promise.all(ours.map((registration) => registration.unregister()))
  // The browser queues unregister behind an install still precaching, which can take minutes,
  // and that install can re-create a cache, so clear the caches now and again once it settles.
  await deleteOfflineCaches(cacheStorage)
  await unregistering
  await deleteOfflineCaches(cacheStorage)
  return ours.length
}

const removeAndReport = async (serviceWorkerContainer, cacheStorage) => {
  try {
    await removeOfflineServiceWorker(serviceWorkerContainer, cacheStorage)
    return 'removed'
  } catch (error) {
    captureOfflineServiceWorkerError(error, 'off')
    return 'failed'
  }
}

/**
 * Registers the offline worker when PRE-204-offline-presenting is on for this user; with the flag
 * off it removes any worker and caches an earlier session left, so turning the flag off is the
 * rollback. A signed-out user counts as flag off: the flag targets signed-in users and their flags
 * never load. A signed-in user whose flags do not load in time (offline, say) keeps everything.
 * A phone or tablet counts as flag off too, so the worker stays desktop-only for this rollout.
 */
export const startOfflineServiceWorker = async ({
  store, serviceWorkerContainer, cacheStorage, serviceWorkerUrl, isSignedIn, isMobile = false, onRegistered,
}) => {
  if (isMobile || !isSignedIn()) return removeAndReport(serviceWorkerContainer, cacheStorage)
  const flagsReady = await store.dispatch('featureToggles/whenAuthFlagsReady', { timeoutMs: FLAGS_READY_TIMEOUT_MS })
  if (!flagsReady) {
    return isSignedIn() ? 'flags-not-ready' : removeAndReport(serviceWorkerContainer, cacheStorage)
  }
  if (!store.getters['featureToggles/canUseOfflinePresenting']) {
    return removeAndReport(serviceWorkerContainer, cacheStorage)
  }
  try {
    await serviceWorkerContainer.register(serviceWorkerUrl)
    onRegistered?.()
    return 'registered'
  } catch (error) {
    captureOfflineServiceWorkerError(error, 'on')
    return 'failed'
  }
}
